import React, { useContext } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { useNavigate, Outlet, useLocation, Link } from "react-router-dom";
import { GlobalContext } from "store/globalContext";
import * as SC from "./CommonLayout.style";
import { MenuItem } from "UI/MenuItem";
import { Confirmation } from "./Confirmation";
import { ErrorDialog } from "./ErrorDialog";
import { WarningDialog } from "./WarningDialog";
import { ReactComponent as SettingsIcon } from "images/settings-icon.svg";
import { ReactComponent as MyButtonDashboardIcon } from "images/my_button_dashboard.svg";
import { ReactComponent as LogoutIcon } from "images/logout-icon.svg";
import { ReactComponent as LogoIcon } from "images/rs-icon.svg";
import { ReactComponent as SignupIcon } from "images/signup-icon.svg";
import { useAuthenticator } from "auth/useAuthenticator";
import AccessDeniedDuePayments from "./AccessDeniedDuePayments";
import { customColors } from "models/customColors";
import { hideMenu } from "store/reducers/isMenuVisibleReducer";
import { Divider } from "@mui/material";
import { DevIndicator } from "./DevIndicator";
import { guideSteps } from "hooks/useOnboarding";
import { Onboarding } from "./Onboarding";
// @ts-ignore
import packageJson from "../../package.json";

const ROUTER_RESET_TO_ROOT_DELAY_IN_MS = 50;

const BottomMenu = ({
  handleLogout,
  hideMenu = () => null,
  senderRegistered = false,
}) => (
  <div className="flex flex-col items-stretch gap-2">
    <div className="flex flex-col gap-0">
      <MenuItem label="Settings" to="/profile" Icon={SettingsIcon} />
      <SC.LogoutItem onClick={handleLogout}>
        <LogoutIcon />
        Log Out
      </SC.LogoutItem>
    </div>
    <Link
      to="https://referral-stream.webflow.io"
      target="_blank"
      onClick={hideMenu}
    >
      <div className="bg-white p-2 flex flex-row justify-start items-start rounded-xl cursor-pointer gap-1">
        <LogoIcon />
        <div className="flex flex-col gap-1">
          <div className="text-sm font-bold text-black">
            Referral Stream’s Website
          </div>
          <div className="text-xs text-gray-500">
            Learn more about Referral Stream
          </div>
        </div>
      </div>
    </Link>
    {!senderRegistered && (
      <Link to="/sign_up/sender" onClick={hideMenu}>
        <div className="bg-white p-2 flex flex-row justify-start items-start rounded-xl cursor-pointer gap-1">
          <SignupIcon />
          <div className="flex flex-col gap-1">
            <div className="text-sm font-bold text-black">Get a Button</div>
            <div className="text-xs text-gray-500">
              Get a Digital Button from other Recipients
            </div>
          </div>
        </div>
      </Link>
    )}
  </div>
);

export const CommonLayout = ({ menu }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuthenticator();
  const isMenuVisible = useAppSelector((state) => state.isMenuVisible);
  const { openConfirmationWithCallback, userData, isGreetingsRead } =
    useContext(GlobalContext);

  const {
    // @ts-ignore
    userData: { fullName, role, email, organization },
    senderData: { id: senderId = false },
  } = useContext(GlobalContext);
  const dispatch = useAppDispatch();

  const handleLogout = () =>
    openConfirmationWithCallback({
      title: "Log Out",
      message: "Do you really want to log out?",
      callbackFn: () => {
        signOut();
        dispatch(hideMenu());
        setTimeout(() => {
          navigate("/");
        }, ROUTER_RESET_TO_ROOT_DELAY_IN_MS);
      },
      buttonLabels: ["No", "Yes"],
    });

  const nameAbbreviation = (fullName ?? role ?? " ")
    .split(" ")
    .map((name) => (name?.length ? name[0] : "").toUpperCase())
    .join("")
    .slice(0, 2);

  const isSuperAdmin = role === "Super Admin";

  return (
    <SC.Container>
      <DevIndicator />
      <div className="text-[10px] text-gray-400 fixed right-5 top-1">
        v{packageJson?.version}
      </div>
      <div
        className={
          "flex-shrink-0 hidden lg:flex flex-col overflow-y-auto justify-between items-stretch p-4 min-w-32 max-w-64"
        }
        style={{ backgroundColor: customColors.lightGray }}
      >
        <div>
          <div
            className={[
              guideSteps.PROFILE,
              "relative rounded-2xl p-2 pb-0",
            ].join(" ")}
            style={{ backgroundColor: customColors.lightGray }}
          >
            <div
              className={[
                "flex flex-row justify-start items-center gap-2",
              ].join(" ")}
            >
              {!!nameAbbreviation && <SC.Avatar>{nameAbbreviation}</SC.Avatar>}
              <div className="flex flex-col items-start gap-1">
                {fullName && <SC.UserName>{fullName}</SC.UserName>}
                <SC.Role>{role}</SC.Role>
                {!!organization?.name && (
                  <SC.Recipient>{organization.name}</SC.Recipient>
                )}
                {!organization?.name && <SC.Email>{email}</SC.Email>}
              </div>
            </div>
            <div className="ml-1 mt-1 mb-4">
              {!!organization?.name && <SC.Email>{email}</SC.Email>}
            </div>
          </div>
          <div className="flex flex-col items-stretch">
            {menu?.map(({ label, path, Icon, subItems, guideClass }) => (
              <MenuItem
                key={`${label}-${path}`}
                label={label}
                to={path}
                Icon={Icon}
                subItems={subItems}
                guideClass={guideClass}
              />
            ))}
            {!!senderId && (
              <>
                <MenuItem
                  label="My Button Dashboard"
                  to="/my_button_dashboard"
                  Icon={MyButtonDashboardIcon}
                />
                <Divider />
              </>
            )}
          </div>
        </div>
        {!!userData?.email && (
          <BottomMenu
            handleLogout={handleLogout}
            senderRegistered={!!senderId || isSuperAdmin}
          />
        )}
      </div>
      {!userData?.organization?.isBlocked ||
      location?.pathname === "/billing" ||
      location?.pathname === "/payments" ||
      location?.pathname === "/payment_method" ||
      location?.pathname?.startsWith("/invoice/") ? (
        <Outlet />
      ) : (
        <AccessDeniedDuePayments />
      )}
      <Confirmation />
      <ErrorDialog />
      <WarningDialog />
      {isMenuVisible && (
        <div className="block lg:hidden">
          <div
            className="mobile-menu absolute top-16 left-0 p-6 pr-8 z-50 rounded shadow-xl max-h-[calc(100vh-65px)] overflow-y-auto"
            style={{ backgroundColor: customColors.lightGray }}
          >
            <div className="flex flex-col items-stretch gap-3">
              {menu?.map(({ label, path, Icon, subItems }) => (
                <MenuItem
                  key={`${label}-${path}`}
                  label={label}
                  to={path}
                  Icon={Icon}
                  subItems={subItems}
                />
              ))}
              {!!senderId && (
                <>
                  <MenuItem
                    label="My Button Dashboard"
                    to="/my_button_dashboard"
                    Icon={MyButtonDashboardIcon}
                  />
                  <Divider />
                </>
              )}
            </div>
            <BottomMenu
              handleLogout={handleLogout}
              hideMenu={() => dispatch(hideMenu())}
              senderRegistered={!!senderId || isSuperAdmin}
            />
          </div>
          <div
            className="absolute top-0 left-0 right-0 bottom-0"
            onClick={() => dispatch(hideMenu())}
          ></div>
        </div>
      )}
      {isGreetingsRead && userData?.role === "Admin" && <Onboarding />}
    </SC.Container>
  );
};
